import React, { ReactElement, ChangeEvent, useEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import {
  ActionsLayout,
  Dialog,
  DialogActionsBar,
} from '@progress/kendo-react-dialogs';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import StringResourceManager, { GetString } from '../StringResourceManager';
import {
  Input,
  InputChangeEvent,
  NumericTextBoxChangeEvent,
  RadioGroup,
  RadioGroupChangeEvent,
} from '@progress/kendo-react-inputs';
import { Grid, Paper } from '@mui/material';
import CardMedia from '@mui/material/CardMedia';
import { Button } from '@progress/kendo-react-buttons';
import { NBCItem, NBCProfile } from '../../Interfaces/NBC/NBCItem';
import { addNewNBCItem } from '../../store/NBC/NBCAction';
import uploadImage from '../../assets/svg/uploadImage.svg';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { uploadIcon } from '@progress/kendo-svg-icons';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { ApplicationState } from '../../store';
import { Error, Label } from '@progress/kendo-react-labels';
import ImageDialog from './ImageDialog';
import DynamicDropDownList from '../DynamicDropDownList';
import { NBCItemGroups } from '../../Interfaces/NBC/NBCItemGroups';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogContainer: {
      display: 'flex',
    },
    paper: {
      border: '1px solid #ccc',
      padding: 16,
    },
    itemContainer: {
      display: 'flex',
      padding: 10,
      justifyContent: 'space-between',
    },
    inputContainer: {
      display: 'flex',
      padding: 10,
      margin: 5,
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    input: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-evenly',
    },
    card: {
      width: 100,
      height: 100,
      cursor: 'pointer',
      border: '1px solid #ccc',
    },
  })
);

interface Props {
  handleCloseDialog: (open: boolean) => void;
  profile: string | undefined;
}

const AddNBCItemDialog: React.FC<Props> = ({
  handleCloseDialog,
  profile,
}): ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const ERRORMSG = GetString('ITEM_CODE_ERROR_LABEL');

  const profileNames: NBCProfile[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.profileNames
  );

  const imageList: string[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.nbcImages
  );

  const nbcItems: NBCItem[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.nbcItems
  );

  const itemsGroups: NBCItemGroups[] = useSelector(
    (state: ApplicationState) => state.nbcReducer.groups
  );

  const [isQuantityRequired, setQuantityRequired] =
    React.useState<boolean>(false);

  const [isWeightRequired, setWeightRequired] = React.useState<boolean>(false);

  const [specialSecurity, setSpecialSecurity] = React.useState<boolean>(false);

  const [isEnabled, setEnabled] = React.useState<boolean>(true);

  const [isVisible, setVisible] = React.useState<boolean>(true);

  const [isPurchaseByDept, setPurchaseByDept] = React.useState<boolean>(false);

  const [layout, setLayout] = React.useState<ActionsLayout>('end');

  const [englishText, setEnglishText] = React.useState<string | undefined>(
    undefined
  );

  const [frenchText, setFrenchText] = React.useState<string | undefined>(
    undefined
  );

  const [spanishText, setSpanishText] = React.useState<string | undefined>(
    undefined
  );

  const [code, setCode] = React.useState<string>('');

  const [price, setPrice] = React.useState<number | null | undefined>();

  const [group, setGroup] = React.useState<string | undefined>(undefined);

  const [isSaveButtonDisabled, setSaveButtonDisabled] = React.useState<
    boolean | undefined
  >(true);

  const [showImageDialog, setShowImageDialog] = React.useState<boolean>(false);

  const [isItemCodeExist, setIsItemCodeExist] = React.useState<boolean>(false);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const isQuantityRequiredChanged = (e: RadioGroupChangeEvent) => {
    setQuantityRequired(e.value);
  };

  const isWeightRequiredChanged = (e: RadioGroupChangeEvent) => {
    setWeightRequired(e.value);
  };

  const isSpecialSecurityChanged = (e: RadioGroupChangeEvent) => {
    setSpecialSecurity(e.value);
  };

  const isEnabledChanged = (e: RadioGroupChangeEvent) => {
    setEnabled(e.value);
  };

  const isVisibleChanged = (e: RadioGroupChangeEvent) => {
    setVisible(e.value);
  };

  const isPurschaseByDeptChanged = (e: RadioGroupChangeEvent) => {
    setPurchaseByDept(e.value);
  };

  const englishInputChanged = (e: InputChangeEvent) => {
    setEnglishText(e.value);
  };

  const frenchInputChanged = (e: InputChangeEvent) => {
    setFrenchText(e.value);
  };

  const spanishInputChanged = (e: InputChangeEvent) => {
    setSpanishText(e.value);
  };

  const codeOnChanged = (e: InputChangeEvent) => {
    setCode(e.value);

    const itemCodeExists = nbcItems.some(item => item.Code === e.value);

    setIsItemCodeExist(itemCodeExists);

    if (e.value === '') setSaveButtonDisabled(true);
    else if (itemCodeExists) setSaveButtonDisabled(true);
    else setSaveButtonDisabled(false);
  };

  const priceOnChanged = (e: NumericTextBoxChangeEvent) => {
    if (e.target.value) setPrice(e.target.value * 100);
  };

  const groupOnChanged = (e: DropDownListChangeEvent) => {
    setGroup(e.value);
  };

  const radioButtonData = [
    { label: GetString('RADIO_BUTTON_YES'), value: true },
    { label: GetString('RADIO_BUTTON_NO'), value: false },
  ];

  const [imagePath, setImagePath] = React.useState<any>();

  const handleClick = () => {
    setShowImageDialog(true);
  };

  const imageSelected = (image: string) => {
    setImagePath(image);
    setShowImageDialog(false);
  };

  const saveOnClick = () => {
    const item: NBCItem = {
      Code: code,
      ImagePath: imagePath,
      Text: { English: englishText, French: frenchText, Spanish: spanishText },
      QuantityRequired: isQuantityRequired,
      WeightRequired: isWeightRequired,
      SpecialSecurity: specialSecurity,
      IsEnabled: isEnabled,
      IsVisible: isVisible,
      PurchaseByDepartment: isPurchaseByDept,
      Price: price === 0 ? undefined : price?.toString(),
      Group: group,
      PLU: code,
      Hits: 0,
      Department: '',
      IsValid: true,
      LastUpdated: new Date(),
    };

    addNewNBCItem(
      dispatch,
      [item],
      profileNames.find(item => item.ProfileUuid === profile)?.ProfileId
    );

    handleClose();
  };

  const cancelOnClick = () => {
    setImagePath('');
    setCode('');
    setEnglishText('');
    setFrenchText('');
    setSpanishText('');
    setPrice(null);
    setQuantityRequired(false);
    setWeightRequired(false);
    setSpecialSecurity(false);
    setPurchaseByDept(false);
    handleCloseDialog(false);
  };

  return (
    <>
      <Dialog
        onClose={handleClose}
        style={{ zIndex: 10000 }}
        title={
          <StringResourceManager
            stringKey="ADD_ITEM_TEXT"
            style="dialogTitle"
            variant="h1"
          />
        }
        width="50vw"
        height="90vh"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <div className={classes.card}>
                  <CardMedia
                    component="img"
                    alt="Uploaded Image"
                    image={imagePath || uploadImage}
                    height={100}
                    width={100}
                  />
                </div>
                <div>
                  <Button
                    onClick={handleClick}
                    style={{ marginLeft: 5, marginRight: 5 }}
                    svgIcon={uploadIcon}
                  >
                    {GetString('SELECT_IMAGE')}
                  </Button>
                </div>
              </div>
              <div className={classes.itemContainer}>
                <div style={{ flexDirection: 'column' }}>
                  <Input
                    type="number"
                    label={GetString('COLUMN_TITLE_CODE')}
                    placeholder={GetString('CODE_PLACEHOLDER')}
                    onChange={codeOnChanged}
                  />
                  {isItemCodeExist && (
                    <Error direction={'start'}>{ERRORMSG}</Error>
                  )}
                </div>
                <NumericTextBox
                  placeholder={GetString('PRICE_PLACEHOLDER')}
                  format="n2"
                  style={{ height: 38, width: 208 }}
                  label={GetString('COLUMN_TITLE_PRICE')}
                  onChange={priceOnChanged}
                />
                <DynamicDropDownList
                  label={GetString('GROUP_LABEL')}
                  data={['', ...itemsGroups.map(item => item.GroupName)]}
                  defaultValue={GetString('SELECT_GROUP')}
                  onChange={groupOnChanged}
                  width={210}
                />
              </div>
              <div className={classes.itemContainer}>
                <Input
                  type="Text"
                  label={GetString('COLUMN_TITLE_ENGLISH')}
                  placeholder={GetString('ENGLISH_PLACEHOLDER')}
                  onChange={englishInputChanged}
                />
                <Input
                  type="Text"
                  label={GetString('COLUMN_TITLE_FRENCH')}
                  placeholder={GetString('FRENCH_PLACEHOLDER')}
                  onChange={frenchInputChanged}
                />
                <Input
                  type="Text"
                  label={GetString('COLUMN_TITLE_SPANISH')}
                  placeholder={GetString('SPANISH_PLACEHOLDER')}
                  onChange={spanishInputChanged}
                />
              </div>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_WEIGHT_REQUIRED')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={isWeightRequired}
                onChange={isWeightRequiredChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_QUANTITY_REQUIRED')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={isQuantityRequired}
                onChange={isQuantityRequiredChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_SPECIAL_SECURITY')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={specialSecurity}
                onChange={isSpecialSecurityChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_ENABLED')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={isEnabled}
                onChange={isEnabledChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_VISIBLE')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={isVisible}
                onChange={isVisibleChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper className={classes.paper}>
              <Label>{GetString('COLUMN_TITLE_PURCHASE_BY_DEPT')}</Label>
              <RadioGroup
                data={radioButtonData}
                defaultValue={isPurchaseByDept}
                onChange={isPurschaseByDeptChanged}
                layout="horizontal"
              />
            </Paper>
          </Grid>
        </Grid>
        <DialogActionsBar layout={layout}>
          <Button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
            style={{ width: 100 }}
            onClick={cancelOnClick}
          >
            {GetString('CANCEL_BUTTON')}
          </Button>
          <Button
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            style={{ width: 100 }}
            onClick={saveOnClick}
            disabled={isSaveButtonDisabled}
          >
            {GetString('SAVE_BUTTON')}
          </Button>
        </DialogActionsBar>
      </Dialog>
      {showImageDialog && (
        <ImageDialog
          handleCloseDialog={() => setShowImageDialog(false)}
          imageList={imageList}
          setSelectedImage={imageSelected}
        />
      )}
    </>
  );
};

export default AddNBCItemDialog;
